import { Input, Modal, Select, Upload, message, Button, Divider } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Title from 'src/branding/Title'
import TextArea from 'antd/lib/input/TextArea'
import {
  sendApproveRequest,
  fetchDepartments,
} from 'src/features/SpecialPurchasesSlice'
import { useDispatch, useSelector } from 'react-redux'
import { getVenueSuppliers } from 'src/features/VenueSuppliersSlice'
import '../../../../features/DashboardStat/DashboardStat.css'
import { UploadOutlined } from '@ant-design/icons'
import useOutsideClick from 'src/utils/useOutsideClick'
import CustomDepartmentSearch from './CustomDepartmentSearch'

const { Option } = Select

function generateUniqueId(prefix = 'id_') {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substr(2, 5)
  return `${prefix}${timestamp}_${random}`
}

const ApproveRequestModal = ({
  approveReqeustModalVisible,
  setApproveReqestModalVisible,
}) => {
  const dispatch = useDispatch()
  const ref = useRef()
  const venueId = useSelector(state => state.venue?.venue?._id)
  const venueSuppliers = useSelector(state =>
    state.venueSuppliers.venueSuppliers.filter(
      venueSupplier => venueSupplier.isActive,
    ),
  )

  const [payload, setPayload] = useState({
    staffName: '',
    staffEmail: '',
    sku: '',
    itemName: '',
    itemCost: 0,
    justification: '',
    managerName: '',
    managerEmail: '',
    selectedSupplier: '',
    department: '',
  })
  const [attachedFile, setAttachedFile] = useState(null)
  const inputRef = useRef(null)
  const departmentRef = useRef(null)
  const [name, setName] = useState('')
  const [venueSupplierOptions, setVenueSupplierOptions] = useState([])
  const [open, setOpen] = useState(false)
  const [openDepartmentDropdown, setOpenDepartmentDropdown] = useState(false)

  const handleChange = (key, value) => {
    setPayload(prev => ({ ...prev, [key]: value }))
  }

  const handleFileChange = info => {
    const file = info.file.originFileObj
    setAttachedFile(file)
  }

  const beforeUpload = file => {
    const isTooLarge = file.size / 1024 / 1024 > 10
    if (isTooLarge) {
      message.error('File must be smaller than 10MB.')
      return Upload.LIST_IGNORE
    }
    return true
  }
  const onNameChange = event => {
    setName(event.target.value)
  }

  const onSubmit = () => {
    // Validate required fields
    if (!payload.department) {
      message.error('Please select a department')
      return
    }
    if (
      !payload.staffName ||
      !payload.staffEmail ||
      !payload.managerName ||
      !payload.managerEmail ||
      !payload.itemName ||
      !payload.itemCost
    ) {
      message.error('Please fill in all required fields')
      return
    }

    const formData = new FormData()

    Object.entries(payload).forEach(([key, value]) => {
      formData.append(key, value)
    })
    if (attachedFile) {
      formData.append('file', attachedFile)
    }

    dispatch(sendApproveRequest(formData, venueId))
    setPayload({
      staffName: '',
      staffEmail: '',
      itemName: '',
      itemCost: 0,
      justification: '',
      managerName: '',
      managerEmail: '',
      department: '',
    })
    setAttachedFile(null)
    setApproveReqestModalVisible(false)
  }

  const onChange = value => {
    const selected = venueSupplierOptions.find(option => {
      const supplier = option?.supplier
      return supplier?._id === value || supplier?.title === value
    })

    handleChange('selectedSupplier', selected?.supplier?.title || value)
  }

  useEffect(() => {
    if (!venueSuppliers || venueSuppliers.length === 0) {
      dispatch(getVenueSuppliers(venueId))
    } else {
      setVenueSupplierOptions(venueSuppliers)
    }
  }, [venueId, venueSuppliers?.length])

  useEffect(() => {
    if (venueId) {
      dispatch(fetchDepartments(venueId))
    }
  }, [dispatch, venueId])

  const addItem = e => {
    e.preventDefault()
    if (!name.trim()) return
    setVenueSupplierOptions([
      ...venueSupplierOptions,
      { supplier: { _id: generateUniqueId(), title: name } },
    ])

    setName('')
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  useOutsideClick(ref, () => {
    if (open) setOpen(false)
  })

  useOutsideClick(departmentRef, () => {
    if (openDepartmentDropdown) setOpenDepartmentDropdown(false)
  })

  return (
    <Modal
      visible={approveReqeustModalVisible}
      onCancel={() => {
        setOpenDepartmentDropdown(false)
        setApproveReqestModalVisible(false)
      }}
      onOk={onSubmit}
      okText={'Send Request'}
    >
      <Wrapper>
        <TitleWrapper>
          <Title>Request Manager Approval</Title>
        </TitleWrapper>
        <DescriptionLine1>
          You may send a request approval to a manager before placing an order
          for high-cost items. The request is sent via email, and the manager
          can approve or decline the order.
        </DescriptionLine1>

        <Row>
          <InputComp
            value={payload.staffName}
            addonBefore={'Staff Name'}
            placeholder='Staff Name'
            onChange={e => handleChange('staffName', e.target.value)}
          />
        </Row>
        <Row>
          <InputComp
            value={payload.staffEmail}
            addonBefore={'Staff Email'}
            placeholder='Staff Email'
            onChange={e => handleChange('staffEmail', e.target.value)}
          />
        </Row>
        <Row>
          <InputComp
            value={payload.managerName}
            addonBefore={'Manager Name'}
            placeholder='Manager Name'
            onChange={e => handleChange('managerName', e.target.value)}
          />
        </Row>
        <Row>
          <InputComp
            value={payload.managerEmail}
            addonBefore={'Manager Email'}
            placeholder='Manager Email'
            onChange={e => handleChange('managerEmail', e.target.value)}
          />
        </Row>
        <Row>
          <div
            style={{
              display: 'flex',
              border: '1px solid #d9d9d9',
              backgroundColor: '#FFFFFF',
              borderRadius: '5px',
            }}
          >
            <div style={{ backgroundColor: '#fafafa' }}>
              <p
                style={{
                  margin: '8px',
                  whiteSpace: 'nowrap',
                }}
              >
                Department
              </p>
            </div>
            <div
              style={{
                width: '1px',
                height: '40px',
                backgroundColor: '#d9d9d9',
              }}
            ></div>
            <div style={{ width: '100%' }}>
              <CustomDepartmentSearch
                ref={departmentRef}
                handleChange={handleChange}
                setOpen={setOpenDepartmentDropdown}
                open={openDepartmentDropdown}
                value={payload.department}
              />
            </div>
          </div>
        </Row>
        <Row>
          <div
            style={{
              display: 'flex',
              border: '1px solid #d9d9d9',
              backgroundColor: '#FFFFFF',
              borderRadius: '5px',
            }}
          >
            <div style={{ backgroundColor: '#fafafa' }}>
              <p
                style={{
                  margin: '8px',
                  whiteSpace: 'nowrap',
                }}
              >
                Supplier
              </p>
            </div>
            <div
              style={{
                width: '1px',
                height: '40px',
                backgroundColor: '#d9d9d9',
              }}
            ></div>
            <Wrapper ref={ref} style={{ width: '100%' }}>
              <Select
                open={open}
                onDropdownVisibleChange={visible => {
                  if (visible) setOpen(true)
                }}
                className='bdr-none'
                placeholder='Supplier'
                style={{
                  width: '100%',
                  //   outline: "none !important",
                  //   boxShadow: "none !important",
                  border: 'transparent !important',
                }}
                dropdownStyle={{
                  border: 'none',
                  boxShadow: 'none',
                }}
                value={payload.selectedSupplier}
                onSelect={() => setOpen(false)}
                onChange={onChange}
                dropdownRender={menu => (
                  <div onClick={e => e.stopPropagation()}>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <div
                      style={{
                        padding: '0 8px 4px',
                        display: 'flex',
                        gap: '8px',
                      }}
                    >
                      <Input
                        placeholder='Please enter item'
                        ref={inputRef}
                        value={name}
                        onChange={onNameChange}
                        onKeyDown={e => e.stopPropagation()}
                        style={{ flex: 1 }}
                      />
                      <Button type='link' icon='plus' onClick={addItem}>
                        Add Supplier
                      </Button>
                    </div>
                  </div>
                )}
              >
                {venueSupplierOptions?.map((supplier, index) => (
                  <Option
                    key={
                      supplier?.supplier?._id ||
                      supplier?._id ||
                      `${supplier?.supplier?.title || 'supplier'}-${index}`
                    }
                    value={supplier?.supplier?._id}
                  >
                    {supplier?.supplier?.title} &nbsp;{' '}
                    {supplier?.supplier?.secondTitle}
                  </Option>
                ))}
              </Select>
            </Wrapper>
          </div>
        </Row>
        <Row>
          <InputComp
            value={payload.sku}
            addonBefore={'SKU'}
            placeholder='SKU'
            onChange={e => handleChange('sku', e.target.value)}
          />
        </Row>

        <Row>
          <InputComp
            value={payload.itemName}
            addonBefore={'Item Name'}
            placeholder='Item Name'
            onChange={e => handleChange('itemName', e.target.value)}
          />
        </Row>

        <Row>
          <InputComp
            value={payload.itemCost}
            addonBefore={'Item Cost'}
            placeholder='Item Cost'
            type='number'
            onChange={e => handleChange('itemCost', e.target.value)}
          />
        </Row>

        <Row>
          <TextArea
            rows={4}
            value={payload.justification}
            placeholder='Justification (Optional)'
            onChange={e => handleChange('justification', e.target.value)}
          />
        </Row>

        <Row>
          {attachedFile ? (
            <FileInfoWrapper>
              <FileName>{attachedFile.name}</FileName>
              <RemoveButton onClick={() => setAttachedFile(null)}>
                ×
              </RemoveButton>
            </FileInfoWrapper>
          ) : (
            <Upload
              beforeUpload={beforeUpload}
              onChange={handleFileChange}
              maxCount={1}
              showUploadList={false}
            >
              <StyledUploadButton>
                <UploadOutlined />
                Attach Document
              </StyledUploadButton>
            </Upload>
          )}
        </Row>
      </Wrapper>
    </Modal>
  )
}
export default ApproveRequestModal

const Wrapper = styled.div``

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 5px;
`
const DescriptionLine1 = styled.div`
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.colours.oliveGrey};
  margin: 5px;
  line-height: 25px;
`

const Row = styled.div`
  margin: 15px 0;
`

const InputComp = styled(Input)`
  width: 100%;
`

const StyledUploadButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px dashed #d9d9d9;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
`

const FileInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border: 1px solid #d9d9d9;
  padding: 8px 12px;
  border-radius: 4px;
  justify-content: space-between;
`

const FileName = styled.span`
  color: #595959;
  font-weight: 500;
  flex-grow: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RemoveButton = styled.span`
  cursor: pointer;
  margin-left: 10px;
  font-size: 18px;
  color: #ff4d4f;

  &:hover {
    color: #ff7875;
  }
`
